// src/components/Countdown.jsx
//
// Optionaler Countdown fuer Spiele mit fester Spielzeit (z. B. "so viele wie
// moeglich in 60 Sekunden"). Zaehlt von der an der Station hinterlegten Dauer
// herunter und klingelt bei 0. Er liefert KEIN Ergebnis – das Ergebnis traegt
// der Betreuer weiterhin selbst ein.
//
// GENAUIGKEIT: wie bei der Stoppuhr ueber einen Zeitstempel (Zielzeit), nicht
// durch Herunterzaehlen eines Intervalls; das Intervall dient nur der Anzeige
// und dem Erkennen von "abgelaufen". Gedrosselte Timer im Hintergrund verfaelschen
// die Restzeit deshalb nicht – nur das Klingeln kaeme dann erst beim Zurueckkehren.

import { useEffect, useRef, useState } from "react";
import { Play, Pause, RotateCcw, BellOff, Bell } from "lucide-react";
import useWakeLock from "../hooks/useWakeLock";
import { audioFreischalten, klingeln } from "../utils/klingeln";

// Unter dieser Restzeit wird die Anzeige hervorgehoben und mit Zehntel gezeigt.
const ENDSPURT_MS = 10000;

function alsUhrzeit(ms) {
  const gesamt = Math.ceil(ms / 1000);
  const min = Math.floor(gesamt / 60);
  const sek = gesamt % 60;
  return `${min}:${String(sek).padStart(2, "0")}`;
}

export default function Countdown({ seconds, disabled = false, hinweis }) {
  const gesamtMs = seconds * 1000;

  // Rest, solange der Countdown NICHT laeuft (bereit, pausiert, abgelaufen).
  const [restMs, setRestMs] = useState(gesamtMs);
  // Zielzeit, solange er laeuft; sonst null.
  const [endTs, setEndTs] = useState(null);
  const [klingelt, setKlingelt] = useState(false);
  const stopKlingelnRef = useRef(null);

  // Tickt nur, damit die Anzeige neu gerendert wird; kein Rechenwert.
  const [, setTick] = useState(0);

  const laeuft = endTs !== null;
  const verbleibendMs = laeuft ? Math.max(0, endTs - Date.now()) : restMs;
  const abgelaufen = !laeuft && restMs <= 0;

  const klingelnBeenden = () => {
    if (stopKlingelnRef.current) {
      stopKlingelnRef.current();
      stopKlingelnRef.current = null;
    }
    setKlingelt(false);
  };

  // Anzeige-Intervall und Ablauf-Erkennung, nur solange er laeuft.
  useEffect(() => {
    if (endTs === null) return undefined;
    // Zwischen zwei Ticks muss React nicht zwingend neu gerendert haben (dann
    // laeuft dieses Intervall noch einmal) – das Klingeln darf trotzdem nur
    // ein einziges Mal starten.
    let abgelaufenGemeldet = false;
    const id = setInterval(() => {
      if (Date.now() >= endTs) {
        if (abgelaufenGemeldet) return;
        abgelaufenGemeldet = true;
        setEndTs(null);
        setRestMs(0);
        setKlingelt(true);
        stopKlingelnRef.current = klingeln(() => {
          stopKlingelnRef.current = null;
          setKlingelt(false);
        });
      } else {
        setTick((t) => t + 1);
      }
    }, 100);
    return () => clearInterval(id);
  }, [endTs]);

  // Beim Schliessen des Dialogs nicht weiterklingeln.
  useEffect(
    () => () => {
      if (stopKlingelnRef.current) stopKlingelnRef.current();
    },
    []
  );

  const bildschirmWach = useWakeLock(laeuft);

  const starten = () => {
    if (disabled || laeuft) return;
    // Muss im Klick laufen: erst dadurch ist der Klingelton spaeter erlaubt.
    audioFreischalten();
    klingelnBeenden();
    const startMs = restMs > 0 ? restMs : gesamtMs; // nach Ablauf: von vorn
    setRestMs(startMs);
    setEndTs(Date.now() + startMs);
  };

  const pausieren = () => {
    if (!laeuft) return;
    setRestMs(Math.max(0, endTs - Date.now()));
    setEndTs(null);
  };

  const zuruecksetzen = () => {
    klingelnBeenden();
    setEndTs(null);
    setRestMs(gesamtMs);
  };

  const imEndspurt = laeuft && verbleibendMs <= ENDSPURT_MS;
  const anzeige = imEndspurt
    ? `${(Math.ceil(verbleibendMs / 100) / 10).toFixed(1).replace(".", ",")} s`
    : alsUhrzeit(verbleibendMs);

  const zeitFarbe = abgelaufen
    ? "text-status-error"
    : imEndspurt
      ? "text-amber-400"
      : laeuft
        ? "text-primary-400"
        : "text-white";

  return (
    <div
      className={`space-y-2 rounded-lg border p-3 ${
        klingelt
          ? "animate-pulse border-red-500 bg-red-900/50"
          : "border-primary-700/40 bg-secondary-900/60"
      }`}
    >
      <div className="flex items-center justify-between">
        <span className="text-xs font-semibold uppercase tracking-wide text-primary-400">
          Countdown ({alsUhrzeit(gesamtMs)})
        </span>
        <span className="text-xs text-secondary-400">klingelt bei 0</span>
      </div>

      <div className={`text-center font-mono text-5xl tabular-nums ${zeitFarbe}`}>
        {abgelaufen ? "0:00" : anzeige}
      </div>

      {abgelaufen && (
        <p className="flex items-center justify-center gap-2 text-center text-sm font-semibold text-red-300">
          <Bell className="h-4 w-4" /> Zeit abgelaufen!
        </p>
      )}

      <div className="flex items-stretch gap-3">
        {klingelt ? (
          <button
            type="button"
            onClick={klingelnBeenden}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-red-600 py-4 text-base font-bold text-white hover:bg-red-500 active:bg-red-700"
          >
            <BellOff className="h-6 w-6" /> Klingeln aus
          </button>
        ) : laeuft ? (
          <button
            type="button"
            onClick={pausieren}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-amber-600 py-4 text-base font-bold text-white hover:bg-amber-500 active:bg-amber-700"
          >
            <Pause className="h-6 w-6" /> Pause
          </button>
        ) : (
          <button
            type="button"
            onClick={starten}
            disabled={disabled}
            className="flex flex-1 items-center justify-center gap-2 rounded-lg bg-green-600 py-4 text-base font-bold text-white hover:bg-green-500 active:bg-green-700 disabled:opacity-50"
          >
            <Play className="h-6 w-6" />
            {abgelaufen ? "Neu starten" : restMs < gesamtMs ? "Weiter" : "Start"}
          </button>
        )}
        <button
          type="button"
          onClick={zuruecksetzen}
          disabled={disabled || (!laeuft && !klingelt && restMs === gesamtMs)}
          title="Zurücksetzen"
          className="flex w-14 flex-none items-center justify-center rounded-lg bg-secondary-700 text-gray-300 hover:bg-secondary-600 disabled:opacity-40"
        >
          <RotateCcw className="h-5 w-5" />
        </button>
      </div>

      {hinweis && !laeuft && !klingelt && <p className="text-xs text-secondary-400">{hinweis}</p>}

      {laeuft && (
        <p className="text-xs text-secondary-400">
          Display an lassen – nur dann klingelt es.
          {bildschirmWach && " · Bildschirm bleibt an."}
        </p>
      )}
    </div>
  );
}
